import { ReactComponent as Timer } from "assets/Timer.svg";
import { optimizeImageCardDesktop } from "helpers/imgix";
import React from "react";
import { useHistory } from "react-router-dom";
import styled from "styled-components/macro";
import { theme } from "theme";

function getPillarColors(pillarId) {
  const map = {
    1: { bg: theme.colors.altBlue, text: theme.colors.heroBlue },
    2: { bg: theme.colors.altPink, text: theme.colors.heroPink },
    3: { bg: theme.colors.altGreen, text: theme.colors.heroGreen },
    4: { bg: theme.colors.altPurple, text: theme.colors.heroPurple },
  };
  return (
    map[pillarId] || { bg: theme.colors.altBlue, text: theme.colors.heroBlue }
  );
}

const CardLi = styled.li`
  display: flex;
  flex-direction: column;
  list-style-type: none;
  background: ${(props) => props.theme.colors.primaryBackground};
  cursor: pointer;
  border-radius: ${(props) => props.theme.borderRadius.lg};
  border: 2px solid
    ${(props) =>
      props.isSelected
        ? `${props.theme.colors.lightBlue}60`
        : props.theme.colors.lightGrey};
  box-shadow: ${(props) =>
    props.isSelected ? props.theme.boxShadow.md : "none"};
  overflow: hidden;
  position: relative;
  outline: none;
  transition: box-shadow 0.2s ease, border-color 0.2s ease;

  a {
    text-decoration: none;
  }

  &:hover,
  &:focus-within {
    border-color: ${(props) => `${props.theme.colors.lightBlue}70`};
    box-shadow: ${(props) => props.theme.boxShadow.md};
  }
`;

const CardImageArea = styled.div`
  height: 140px;
  background: linear-gradient(
    135deg,
    ${(props) => props.theme.colors.altBackground},
    ${(props) => props.theme.colors.lightGrey}
  );
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
`;

const CardImage = styled.img`
  position: absolute;
  inset: 0;
  width: 100%;
  height: 100%;
  object-fit: cover;
`;

const SelectOverlay = styled.div`
  position: absolute;
  inset: 0;
  background: ${(props) =>
    props.isSelected
      ? `${props.theme.colors.heroBlue}25`
      : "rgba(255,255,255,0.15)"};
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;
`;

const CheckBadge = styled.div`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: ${(props) =>
    props.isSelected ? props.theme.colors.heroBlue : "white"};
  border: 2px solid
    ${(props) =>
      props.isSelected
        ? props.theme.colors.heroBlue
        : props.theme.colors.lightGrey};
  color: ${(props) =>
    props.isSelected ? "white" : props.theme.colors.lightGrey};
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  transition: background 0.2s ease, border-color 0.2s ease, color 0.2s ease;
`;

const CardBody = styled.div`
  padding: ${(props) => props.theme.spacing.sm}
    ${(props) => props.theme.spacing.md};
  display: flex;
  flex-direction: column;
  flex: 1;
  margin-bottom: ${(props) => props.theme.spacing.md};
`;

const CardTitle = styled.h2`
  font-size: ${(props) => props.theme.fontSize.xs};
  font-weight: 700;
  color: ${(props) => props.theme.colors.heading};
  margin: 0 0 6px 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
  line-height: 1.4;
`;

const CategoryBadge = styled.span`
  width: fit-content;
  border-radius: 20px;
  padding: 2px 8px;
  font-size: ${(props) => props.theme.fontSize.xxs};
  font-weight: 600;
  background: ${(props) => props.bgColor};
  color: ${(props) => props.textColor};
  margin-bottom: 6px;
`;

const CardDescription = styled.p`
  font-size: ${(props) => props.theme.fontSize.xxs};
  color: ${(props) => props.theme.colors.body};
  line-height: 1.5;
  margin: 0 0 0 0;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 2;
  overflow: hidden;
`;

const CardFooter = styled.div`
  display: flex;
  align-items: center;
  margin-top: auto;
  padding-top: ${(props) => props.theme.spacing.sm};
`;

const TimerContainer = styled.div`
  display: flex;
  height: 16px;
  align-items: center;
  gap: 4px;
  font-size: ${(props) => props.theme.fontSize.xxs};
  color: ${(props) => props.theme.colors.body};

  svg {
    height: 100%;
    width: fit-content;
  }
`;

export default function AllyCard({ select, link, data, editCard }) {
  const history = useHistory();
  const isSelected = editCard && data?.active === 1;

  const buttonToggle = (id) => {
    return editCard ? select(id) : history.push(link);
  };

  return (
    <CardLi
      role="menuitem"
      aria-label={
        data.active === 1
          ? "Card is added to your program."
          : "Card is removed from your program."
      }
      isSelected={isSelected}
      tabIndex="0"
      onClick={() => buttonToggle(data.id)}
      data-testid={`allyCard${data.id}`}
    >
      <CardImageArea>
        <CardImage
          loading="lazy"
          src={optimizeImageCardDesktop(
            data?.image_url ? data?.image_url : data?.image
          )}
          alt="decorative"
        />
        {editCard && (
          <SelectOverlay isSelected={isSelected}>
            <CheckBadge isSelected={isSelected}>&#10003;</CheckBadge>
          </SelectOverlay>
        )}
      </CardImageArea>

      <CardBody>
        <CardTitle>{data?.title}</CardTitle>

        {data?.pillar && <PillarBadge pillar={data.pillar} />}

        <CardDescription>
          {data?.external_description || data?.extract}
        </CardDescription>

        {data?.lesson_time && (
          <CardFooter>
            <TimerContainer>
              <Timer aria-label="time indicator" />
              {data.lesson_time}
            </TimerContainer>
          </CardFooter>
        )}
      </CardBody>
    </CardLi>
  );
}

function PillarBadge({ pillar }) {
  const colors = getPillarColors(pillar?.id);
  return (
    <CategoryBadge bgColor={colors.bg} textColor={colors.text}>
      {pillar.title}
    </CategoryBadge>
  );
}
